import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, Baby, Heart, Activity, Syringe, 
  Microscope, Thermometer, Users, Shield, Eye, Monitor, ArrowRight, Globe 
} from 'lucide-react';
import ScheduleModal from '../components/ScheduleModal';

const Services = () => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const pediatricServices = [
    {
      icon: <Baby className="w-12 h-12" />,
      title: 'Pediatric Care',
      description: 'Comprehensive healthcare for infants, children, and adolescents (newborn through age 17) in DeLand and Orange City. Dr. Concepcion Anayas, M.D. and Pam Perry, ARNP provide expert pediatric care.',
      features: ['Well-Child Visits', 'Immunizations', 'School & Sports Physicals', 'Growth & Development'],
    },
    {
      icon: <Syringe className="w-12 h-12" />,
      title: 'Childhood Immunizations',
      description: 'Complete vaccination services for children at our Orange City and DeLand locations, following CDC guidelines for childhood vaccines.',
      features: ['Childhood Vaccines', 'Flu Shots', 'School Required Immunizations', 'Vaccine Records'],
    },
    {
      icon: <Thermometer className="w-12 h-12" />,
      title: 'Pediatric Sick Visits',
      description: 'Same-day appointments for sudden illnesses in children. Our pediatric providers treat ear infections, fevers, colds, flu, and more.',
      features: ['Ear Infections', 'Respiratory Illnesses', 'Fever Treatment', 'Minor Injuries'],
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Special Needs Pediatrics',
      description: 'Specialized pediatric services for children with developmental disabilities and special healthcare needs in West Volusia.',
      features: ['ADHD Screening', 'Developmental Support', 'Care Coordination', 'Behavioral Health'],
    },
  ];

  const adultServices = [
    {
      icon: <Stethoscope className="w-12 h-12" />,
      title: 'Internal Medicine',
      description: 'Comprehensive primary care for adults (18 and older) throughout West Volusia County. Dr. Marcelo Anayas, M.D. provides adult primary care services.',
      features: ['Annual Physicals', 'Health Screenings', 'Preventive Care', 'Medication Management'],
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: 'Chronic Disease Management',
      description: 'Ongoing care and support for adult patients in DeLand and Orange City with diabetes, hypertension, heart disease, and other chronic conditions.',
      features: ['Diabetes Care', 'Hypertension Management', 'Heart Disease', 'Cholesterol Management'],
    },
    {
      icon: <Activity className="w-12 h-12" />,
      title: 'Adult Preventive Medicine',
      description: 'Proactive healthcare services for DeLand and Orange City adult patients to prevent illness and promote long-term wellness.',
      features: ['Health Screenings', 'Cancer Screenings', 'Lifestyle Counseling', 'Weight Management'],
    },
  ];

  const sharedServices = [
    {
      icon: <Monitor className="w-12 h-12" />,
      title: 'Telehealth Services',
      description: 'Virtual healthcare consultations for West Volusia families. Connect with our providers from home for follow-ups and minor concerns.',
      features: ['Video Consultations', 'Follow-up Visits', 'Prescription Refills', 'Remote Care'],
    },
    {
      icon: <Microscope className="w-12 h-12" />,
      title: 'Laboratory Services',
      description: 'Community Medical Center of West Volusia does not perform on-site laboratory testing. Specimen collection and cultures are performed at our clinic locations and sent to an external reference laboratory the same day to ensure timely and accurate results. Patients are notified promptly once results are received and reviewed by a provider.',
      features: ['Blood Tests', 'Urinalysis', 'Rapid Testing', 'Screening Tests'],
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-teal-600 text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1611764461465-09162da6465a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc2MDU4NzU0OHww&ixlib=rb-4.1.0&q=85" 
            alt="Healthcare services in DeLand and Orange City Florida" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-5xl font-bold mb-6">Pediatrics & Internal Medicine Services</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Comprehensive healthcare for children and adults in DeLand and Orange City. Pediatric care for newborns through adolescents. Internal medicine for adults 18 and older.
          </p>
        </div>
      </section>

      {/* Pediatric Services */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Pediatric Services</h2>
            <p className="text-lg text-gray-600">For children from newborn through adolescence (0-17 years)</p>
            <p className="text-sm text-blue-600 mt-2">Providers: Dr. Concepcion Anayas, M.D. • Pam Perry, ARNP</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {pediatricServices.map((service, index) => (
              <article
                key={index}
                data-testid={`pediatric-service-card-${index}`}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 overflow-hidden border border-gray-100 flex flex-col h-full"
              >
                <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-6">
                  <div className="text-blue-600 mb-4" aria-hidden="true">{service.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <p className="text-gray-600 mb-6 flex-grow">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3" aria-hidden="true"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Adult Services */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Internal Medicine Services</h2>
            <p className="text-lg text-gray-600">For adult patients 18 years and older</p>
            <p className="text-sm text-blue-600 mt-2">Provider: Dr. Marcelo Anayas, M.D.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {adultServices.map((service, index) => (
              <article
                key={index}
                data-testid={`adult-service-card-${index}`}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 overflow-hidden border border-gray-100 flex flex-col h-full"
              >
                <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-6">
                  <div className="text-teal-600 mb-4" aria-hidden="true">{service.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <p className="text-gray-600 mb-6 flex-grow">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <div className="w-2 h-2 bg-teal-600 rounded-full mr-3" aria-hidden="true"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Shared Services */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Additional Services</h2>
            <p className="text-lg text-gray-600">Available for all patients at both locations</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {sharedServices.map((service, index) => (
              <article
                key={index}
                data-testid={`shared-service-card-${index}`}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden border border-gray-100 flex flex-col h-full"
              >
                <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-6">
                  <div className="text-blue-600 mb-4" aria-hidden="true">{service.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <p className="text-gray-600 mb-6 flex-grow">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3" aria-hidden="true"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                What to Expect at Your Visit
              </h2>
              <div className="space-y-6 text-gray-700">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">New Patients Welcome</h3>
                  <p>
                    Please bring a valid ID, insurance card (Medicaid accepted), 
                    and immunization records for children. Arrive 15 minutes early to complete registration.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Insurance & Medicaid</h3>
                  <p>
                    We accept most major insurance plans, Medicare, and <strong>Medicaid</strong> (Sunshine Health, Humana, 
                    Florida Community Care). We also offer a sliding fee scale for uninsured patients.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Walk-Ins Welcome</h3>
                  <p>
                    Need a same-day appointment? Walk-ins are welcome at both DeLand and Orange City locations. 
                    Saturday hours available (8 AM - 1 PM, call to verify).
                  </p>
                </div>
                <div className="flex items-start gap-3 bg-teal-50 p-4 rounded-lg">
                  <Globe className="text-teal-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Se Habla Español</h3>
                    <p className="text-sm">Our clinic has bilingual staff who speak Spanish to better serve our community.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <img 
                src="https://images.pexels.com/photos/40568/medical-appointment-doctor-healthcare-40568.jpeg" 
                alt="Medical appointment at Community Medical Center in West Volusia" 
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-6">Schedule Your Appointment Today</h2>
          <p className="text-xl mb-10 text-blue-200">
            Pediatrics for children • Internal Medicine for adults • Medicaid accepted • Walk-ins welcome
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowScheduleModal(true)}
              data-testid="services-schedule-btn"
              className="inline-flex items-center justify-center bg-white text-blue-700 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-50 transition-all cursor-pointer"
            >
              Schedule Online
            </button>
            <a
              href="tel:386-775-1792"
              data-testid="services-call-oc-btn"
              className="inline-flex items-center justify-center bg-blue-800 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-900 transition-all"
            >
              Orange City: (386) 775-1792
            </a>
            <a
              href="tel:386-738-1792"
              data-testid="services-call-dl-btn"
              className="inline-flex items-center justify-center bg-blue-800 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-900 transition-all"
            >
              DeLand: (386) 738-1792
            </a>
          </div>
        </div>
      </section>

      <ScheduleModal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} />
    </div>
  );
};

export default Services;
